import React, { useState } from 'react';
import Modal from './Modal';
import ImageModal from './ImageModal';
import SecureMarkdownRenderer from './SecureMarkdownRenderer';
import { getImageUrl } from '../utils/helpers';

function SessionViewer({ session, plan, isOpen, onClose }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  if (!session || !plan) return null;

  const getResultIcon = (result) => {
    switch (result.toLowerCase()) {
      case 'pass': return '✅';
      case 'fail': return '❌';
      case 'skip': return '⏭️';
      case 'testing': return '🔄';
      default: return '⚪';
    }
  };

  const getStepStatus = (stepId) => {
    const stepResult = session.steps.find(s => s.step_id === stepId);
    return stepResult ? stepResult.result.toLowerCase() : 'untested';
  };

  const getQuestionStepResult = (stepId) => {
    const stepResult = session.steps.find(s => s.step_id === stepId);
    if (!stepResult || !stepResult.answers || stepResult.answers.length === 0) {
      return 'Not Answered';
    }
    return 'Answered';
  };

  const formatQuestionAnswers = (step, stepResult) => {
    if (!stepResult.answers || stepResult.answers.length === 0) {
      return <span style={{ color: '#6b7280', fontStyle: 'italic' }}>No answers provided</span>;
    }

    if (step.question?.type === 'evaluate') {
      // For evaluation questions, show each option with its evaluation
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {step.question.options.map((option, optIndex) => {
            const passAnswer = `${option}:pass`;
            const failAnswer = `${option}:fail`;
            const hasPass = stepResult.answers.includes(passAnswer);
            const hasFail = stepResult.answers.includes(failAnswer);
            
            let icon = '❓';
            let color = '#6b7280';
            let status = 'Not Evaluated';
            
            if (hasPass) {
              icon = '✅';
              color = '#10b981';
              status = 'Pass';
            } else if (hasFail) {
              icon = '❌';
              color = '#ef4444';
              status = 'Fail';
            }
            
                         return (
               <div key={optIndex} style={{ 
                 padding: '0.5rem',
                 backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f8fafc',
                 borderRadius: '6px',
                 border: '1px solid #e2e8f0'
               }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                   <span style={{ color, fontSize: '1.2rem' }}>{icon}</span>
                   <span style={{ flex: 1, fontWeight: '500' }}>{option}</span>
                   <span style={{ color, fontSize: '0.875rem', fontWeight: '600' }}>{status}</span>
                 </div>
                 {step.question.option_images?.[option] && step.question.option_images[option].length > 0 && (
                   <div className="image-preview" style={{ marginTop: '0.5rem' }}>
                     {step.question.option_images[option].map((image, imgIndex) => (
                       <img 
                         key={imgIndex} 
                         src={getImageUrl(image, plan?.id)} 
                                                                  alt={`${option} ${imgIndex + 1}`}
                         style={{ cursor: 'pointer', maxHeight: '80px' }}
                         onClick={() => {
                           setSelectedImage(image);
                           setShowImageModal(true);
                         }}
                       />
                     ))}
                   </div>
                 )}
               </div>
             );
          })}
        </div>
      );
    } else {
      // For single/multi select, show selected options as badges
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {stepResult.answers.map((answer, answerIndex) => (
            <span
              key={answerIndex}
              className="answer-badge"
              style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '16px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {answer}
            </span>
          ))}
        </div>
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Test Session: ${session.username} on ${session.platform}`} wide={true}>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div className="session-info" style={{ marginBottom: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
          <p><strong>Tester:</strong> {session.username}</p>
          <p><strong>Platform:</strong> {session.platform}</p>
          <p><strong>Started:</strong> {new Date(session.started_at).toLocaleString()}</p>
        </div>

        <div>
          <h4>Test Results</h4>
          {(() => {
            // Find the test case that this session belongs to
            const testCase = plan.test_cases?.find(tc => tc.id === session.test_case_id);
            if (!testCase || !testCase.steps) return <p>No test case found</p>;
            
            return testCase.steps.map((step) => {
              const stepResult = session.steps.find(s => s.step_id === step.id);
              if (!stepResult) return null;

            return (
              <div key={step.id} className="step-item" style={{ marginBottom: '1rem' }}>
                <div className="step-header" style={{ cursor: 'default' }}>
                  <div>
                    <strong>{step.title}</strong>
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {step.step_type === 'question' ? '❓ Question' : '🧪 Test'}
                    </span>
                    <span className={`step-status ${getStepStatus(step.id)}`} style={{ marginLeft: '1rem' }}>
                      {step.step_type === 'question' ? (
                        <>
                          {getQuestionStepResult(step.id) === 'Answered' ? '✅' : '❓'}
                          {getQuestionStepResult(step.id)}
                        </>
                      ) : (
                        <>
                          {getResultIcon(stepResult.result)}
                          {stepResult.result}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="step-content">
                  <SecureMarkdownRenderer style={{ color: '#6b7280', marginBottom: '1rem' }}>{step.description}</SecureMarkdownRenderer>
                  
                  {step.step_type === 'question' && step.question && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Question Type:</strong>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                        {step.question.type === 'single' ? 'Single Select' :
                         step.question.type === 'multi' ? 'Multi Select' :
                         step.question.type === 'evaluate' ? 'Evaluate Items' : step.question.type}
                        {step.question.required && <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>}
                      </span>
                      
                      {/* Question Images */}
                      {step.question.images && step.question.images.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Question Images:</div>
                          <div className="image-preview">
                            {step.question.images.map((image, imgIndex) => (
                              <img 
                                key={imgIndex} 
                                src={getImageUrl(image, plan?.id)} 
                                                                 alt={`Question ${imgIndex + 1}`}
                                style={{ cursor: 'pointer', maxHeight: '120px' }}
                                onClick={() => {
                                  setSelectedImage(image);
                                  setShowImageModal(true);
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {step.step_type === 'question' && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Answers:</strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        {formatQuestionAnswers(step, stepResult)}
                      </div>
                    </div>
                  )}
                  
                  {stepResult.notes && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Notes:</strong>
                      <div className="notes-box" style={{ marginTop: '0.25rem', padding: '0.5rem', background: '#f8fafc', borderRadius: '4px' }}>
                        <SecureMarkdownRenderer>{stepResult.notes}</SecureMarkdownRenderer>
                      </div>
                    </div>
                  )}

                  {stepResult.images && stepResult.images.length > 0 && (
                    <div>
                      <strong>Screenshots:</strong>
                      <div className="image-preview" style={{ marginTop: '0.5rem' }}>
                        {stepResult.images.map((image, imgIndex) => (
                          <img 
                            key={imgIndex} 
                            src={getImageUrl(image, plan?.id)} 
                            alt={`Screenshot ${imgIndex + 1}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedImage(image);
                              setShowImageModal(true);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            });
          })()}
        </div>
      </div>

      <div className="modal-buttons">
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
        imageSrc={getImageUrl(selectedImage, plan?.id)}
        imageAlt="Session screenshot"
      />
    </Modal>
  );
}

export default SessionViewer; 